'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '../lib/supabase/client';
import { normalizeRole, getRoleDashboardPath as _getRoleDashboardPath, CanonicalRole } from '../lib/roleRouting';

// Re-export for backward compatibility
export { getRoleDashboardPath } from '../lib/roleRouting';

interface UserProfile {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  role: CanonicalRole;
  profile_photo?: string;
  country?: string;
  language?: string;
  created_at?: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<any>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (newPassword: string) => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
  getRoleDashboard: (role?: string) => string;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  // Track whether the initial session check has completed
  const initialCheckDoneRef = useRef(false);
  // Prevent state updates after unmount
  const mountedRef = useRef(true);
  // Safety net timer ref
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchProfile = async (userId: string): Promise<void> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (!error && data && mountedRef.current) {
        const normalized = { ...data, role: normalizeRole(data.role) };
        setProfile(normalized as UserProfile);
      }
    } catch {
      // Profile fetch failed silently — app still works with user_metadata role
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    // Safety net: always clear loading after 8s
    safetyTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setLoading(false);
    }, 8000);

    // STEP 1: Do the initial session check synchronously using getSession().
    // This runs ONCE on mount and sets the initial state without triggering
    // onAuthStateChange's INITIAL_SESSION event to cause a second render cycle.
    const initAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();

        if (!mountedRef.current) return;

        if (initialSession?.user) {
          setSession(initialSession);
          setUser(initialSession.user);
          // Fetch profile — setLoading(false) happens in finally
          await fetchProfile(initialSession.user.id);
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
          if (mountedRef.current) setLoading(false);
        }
      } catch {
        if (mountedRef.current) setLoading(false);
      } finally {
        initialCheckDoneRef.current = true;
        // Clear the safety net since we completed normally
        if (safetyTimerRef.current) {
          clearTimeout(safetyTimerRef.current);
          safetyTimerRef.current = null;
        }
      }
    };

    initAuth();

    // STEP 2: Subscribe to subsequent auth changes (login, logout, token refresh).
    // Skip the INITIAL_SESSION event since we already handled it above.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, currentSession) => {
      // Skip INITIAL_SESSION — already handled by getSession() above
      if (event === 'INITIAL_SESSION') return;

      if (!mountedRef.current) return;

      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        // For SIGNED_IN / TOKEN_REFRESHED: fetch profile in background
        // Don't set loading=true here — we're already past the initial load
        setTimeout(() => {
          if (mountedRef.current) {
            fetchProfile(currentSession.user.id);
          }
        }, 0);
      } else {
        // SIGNED_OUT
        setProfile(null);
        if (mountedRef.current) setLoading(false);
      }
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Email/Password Sign Up with role and profile creation
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const rawRole = metadata?.role || 'tourist';
    // Normalize role — ensure we use the canonical DB enum value
    const canonicalRole = normalizeRole(rawRole);
    // Normalize email
    const normalizedEmail = email.trim().toLowerCase();

    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          role: canonicalRole,
          phone: metadata?.phone || '',
          country: metadata?.country || 'Nepal',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback?type=signup`,
      },
    });
    if (error) throw error;

    // If user is immediately available (email auto-confirmed), create profile
    if (data?.user) {
      try {
        await supabase.from('users').upsert(
          {
            id: data.user.id,
            email: normalizedEmail,
            full_name: metadata?.fullName || '',
            role: canonicalRole,
            phone: metadata?.phone || null,
            country: metadata?.country || 'Nepal',
            language: 'en',
          },
          { onConflict: 'id' }
        );
      } catch {
        // Profile creation handled by DB trigger as fallback
      }
    }

    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string, rememberMe = false) => {
    // Normalize email: trim whitespace and convert to lowercase
    const normalizedEmail = email.trim().toLowerCase();

    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    if (error) {
      // Provide user-friendly error messages
      if (error.message?.toLowerCase().includes('invalid login credentials') ||
          error.message?.toLowerCase().includes('invalid_credentials')) {
        throw new Error('Incorrect email or password. Please check your credentials and try again.');
      }
      if (error.message?.toLowerCase().includes('email not confirmed') ||
          error.message?.toLowerCase().includes('email_not_confirmed')) {
        throw new Error('Please verify your email address before signing in. Check your inbox for a confirmation link.');
      }
      if (error.message?.toLowerCase().includes('too many requests') ||
          error.message?.toLowerCase().includes('rate_limit')) {
        throw new Error('Too many login attempts. Please wait a few minutes and try again.');
      }
      if (error.message?.toLowerCase().includes('network') ||
          error.message?.toLowerCase().includes('fetch')) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      throw error;
    }

    if (rememberMe) {
      try {
        localStorage.setItem('yn_remember_me', 'true');
      } catch {}
    }

    return data;
  };

  // Sign Out
  const signOut = async () => {
    try {
      localStorage.removeItem('yn_remember_me');
    } catch {}
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  // Forgot Password
  const forgotPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
    });
    if (error) throw error;
  };

  // Reset Password (after clicking email link)
  const resetPassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null && user?.email_confirmed_at !== undefined;
  };

  // Get User Profile from Database
  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return { ...data, role: normalizeRole(data.role) } as UserProfile;
  };

  // Refresh profile
  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  // Get dashboard path for a given role
  const getRoleDashboard = (role?: string): string => {
    // Prefer DB profile role > user_metadata role > fallback
    const effectiveRole = role || profile?.role || user?.user_metadata?.role;
    return _getRoleDashboardPath(effectiveRole);
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    forgotPassword,
    resetPassword,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
    getRoleDashboard,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
